import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { loadSegmentSummary } from "../graphql/queries/listSegments";

const normalizeIds = (segmentIds) => {
  if (typeof segmentIds === "string") return [segmentIds];
  if (Array.isArray(segmentIds)) return segmentIds;
  return [];
};

const uniqueIds = (ids) => Array.from(new Set(ids.filter(Boolean)));

const useSegmentSummaries = (segmentIds, options = {}) => {
  const { fetchPolicy = "cache-first" } = options;
  const ids = useMemo(() => uniqueIds(normalizeIds(segmentIds)), [segmentIds]);

  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [loadSegmentSummaryCall] = useLazyQuery(loadSegmentSummary, {
    fetchPolicy,
  });

  useEffect(() => {
    if (!ids.length) {
      setSegments([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    if (!loadSegmentSummaryCall) {
      setSegments([]);
      setLoading(false);
      return;
    }

    Promise.all(
      ids.map((id) =>
        loadSegmentSummaryCall({
          variables: { id },
          fetchPolicy,
        })
      )
    )
      .then((results) => {
        const fetchedSegments =
          results
            ?.map((result, index) => {
              const segment = result?.data?.loadSegment;
              const segmentId = ids[index];
              if (!segment) {
                console.warn(`Segment with id ${segmentId} not found or invalid`);
                return null;
              }
              return segment;
            })
            .filter(Boolean) || [];
        setSegments(fetchedSegments);
      })
      .catch((err) => {
        console.error("Error fetching segment summaries:", err);
        setSegments([]);
        setError(err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, fetchPolicy]);

  return { segments, loading, error };
};

export default useSegmentSummaries;
